import React, { useEffect, useCallback } from 'react';
import { X } from 'lucide-react';
import { SHORTCUT_GROUPS } from '../hooks/useKeyboardShortcuts';

interface KeyboardShortcutsHelpProps {
  open: boolean;
  onClose: () => void;
}

const Key = ({ children }: { children: React.ReactNode }) => (
  <kbd
    className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded border text-xs font-mono font-semibold"
    style={{ background: 'var(--bg-tertiary)', borderColor: 'var(--border-primary)', color: 'var(--text-primary)', boxShadow: '0 1px 1px rgba(0,0,0,.1)' }}
  >
    {children}
  </kbd>
);

const KeyboardShortcutsHelp = ({ open, onClose }: KeyboardShortcutsHelpProps) => {
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape') onClose();
  }, [onClose]);

  useEffect(() => {
    if (open) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [open, handleEscape]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

      {/* Modal */}
      <div
        className="relative rounded-xl shadow-2xl border max-w-lg w-full mx-4 overflow-hidden"
        style={{ background: 'var(--bg-secondary)', borderColor: 'var(--border-primary)' }}
        data-testid="keyboard-shortcuts-help"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'var(--border-secondary)' }}>
          <h2 className="text-lg font-semibold text-wmt-primary">Keyboard Shortcuts</h2>
          <button onClick={onClose} className="p-1 rounded-lg transition hover:opacity-70">
            <X size={18} className="text-wmt-muted" />
          </button>
        </div>

        <div className="px-6 py-4 space-y-6 max-h-[60vh] overflow-y-auto">
          {SHORTCUT_GROUPS.map((group) => (
            <div key={group.title}>
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: 'var(--text-tertiary)' }}>
                {group.title}
              </h3>
              <div className="space-y-2">
                {group.shortcuts.map(({ keys, description }) => (
                  <div key={description} className="flex items-center justify-between py-1">
                    <span className="text-sm text-wmt-secondary">{description}</span>
                    <div className="flex items-center gap-1">
                      {keys.map((key, i) => (
                        <React.Fragment key={i}>
                          {i > 0 && <span className="text-xs text-wmt-light mx-0.5">then</span>}
                          <Key>{key}</Key>
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="px-6 py-3 border-t text-center" style={{ borderColor: 'var(--border-secondary)' }}>
          <span className="text-xs text-wmt-light">Press <Key>?</Key> or <Key>Esc</Key> to close</span>
        </div>
      </div>
    </div>
  );
};

export default KeyboardShortcutsHelp;
